import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Lightbulb, Loader2, Search } from 'lucide-react';
import { Idea } from '../../types/idea-machine';
import { ideasService } from '../../services/ideas';
import { IdeaCard } from '../../components/idea-machine/IdeaCard';
import { IdeaDialog } from '../../components/idea-machine/IdeaDialog';
import { db } from '../../config/firebase';
import { doc, deleteDoc } from 'firebase/firestore';
import { Input } from '../../components/ui/input';

export default function YourIdeas() {
  const { userEmail } = useAuth();
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [filteredIdeas, setFilteredIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    async function fetchIdeas() {
      if (!userEmail) return;

      try {
        console.log('Fetching ideas for user:', userEmail);
        const { ideas: ideasData, total } = await ideasService.getUserIdeas(userEmail, currentPage);
        console.log('Fetched ideas:', ideasData);
        setIdeas(ideasData);
        setFilteredIdeas(ideasData);
        setTotalPages(Math.ceil(total / 10));
      } catch (error) {
        console.error('Error fetching ideas:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchIdeas();
  }, [userEmail, currentPage]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredIdeas(ideas);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = ideas.filter(idea => {
      const contentMatch = idea.content.some(c => c.toLowerCase().includes(query));
      const seedPromptMatch = idea.seedPrompt && idea.seedPrompt.toLowerCase().includes(query);
      return contentMatch || seedPromptMatch;
    });
    setFilteredIdeas(filtered);
  }, [searchQuery, ideas]);

  const handleDeleteIdea = async (ideaId: string) => {
    try {
      const ideaRef = doc(db, 'ideas', ideaId);
      await deleteDoc(ideaRef);
      console.log('Idea deleted successfully:', ideaId);
      const updatedIdeas = ideas.filter(idea => idea.id !== ideaId);
      setIdeas(updatedIdeas);
      // Re-apply filter if needed
      if (searchQuery.trim()) {
         const query = searchQuery.toLowerCase();
         setFilteredIdeas(updatedIdeas.filter(idea =>
            idea.content.some(c => c.toLowerCase().includes(query)) ||
            (idea.seedPrompt && idea.seedPrompt.toLowerCase().includes(query))
         ));
      } else {
         setFilteredIdeas(updatedIdeas);
      }
    } catch (error) {
      console.error('Error deleting idea:', error);
      alert('Failed to delete idea. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 dark:text-white">Your Ideas</h1>
        <Card className="p-8 text-center bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-sm">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="text-gray-500 dark:text-gray-400">Loading your ideas...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Your Ideas</h1>
        
        {/* Search Input */}
        <div className="relative w-full md:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500 dark:text-gray-400" />
          <Input
            type="text"
            placeholder="Search ideas..."
            className="pl-9 bg-white dark:bg-gray-800"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {ideas.length === 0 ? (
        <Card className="p-12 text-center bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-gray-100 dark:bg-gray-700/50 rounded-full mb-2">
              <Lightbulb className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">No Ideas Yet</h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              Visit the Idea Launchpad to start generating ideas from creative seeds!
            </p>
          </div>
        </Card>
      ) : filteredIdeas.length === 0 ? (
        <Card className="p-12 text-center bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-gray-100 dark:bg-gray-700/50 rounded-full mb-2">
              <Search className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">No Matching Ideas</h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              Try adjusting your search terms to find what you're looking for.
            </p>
          </div>
        </Card>
      ) : (
        <div>
          <div className="grid gap-4 animate-in fade-in">
            {filteredIdeas.map((idea) => (
              <IdeaCard
                key={idea.id}
                idea={idea}
                onClick={() => {
                  setSelectedIdea(idea);
                  setDialogOpen(true);
                }}
                onDelete={() => handleDeleteIdea(idea.id)}
                showDelete={true}
                onTogglePrivacy={(isPublic) => {
                  ideasService.togglePrivacy(idea.id, isPublic);
                  // Optimistic update
                  const updatedIdea = { ...idea, isPublic };
                  setIdeas(ideas.map(i => i.id === idea.id ? updatedIdea : i));
                  // Re-apply filters if needed
                  if (!searchQuery.trim()) {
                     setFilteredIdeas(ideas.map(i => i.id === idea.id ? updatedIdea : i)); 
                  }
                }}
              />
            ))}
          </div>
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <IdeaDialog
        idea={selectedIdea}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
